import * as React from 'react';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import { isEqual, compact, isEmpty } from 'lodash';
import { OrderedMap } from 'immutable';
import cx from 'classnames';

import { ActionTypes } from 'groups/constants/GroupConstants';
import { dispatcher } from 'lib/Dispatcher';
// @ts-ignore
import { putMessageRecipientsForTargets, allTargetsAreGroups } from 'lib/RoleBasedMessagingUtils';
// @ts-ignore
import CharacterCount from 'lib/CharacterCount';
// @ts-ignore
import Composer from 'chat/components/Composer';
// @ts-ignore
import ComposerLinkPreview from 'chat/components/ComposerLinkPreview';
import CurrentUser from 'auth/CurrentUser';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import Message from 'entities/Message';
import ToastActions from 'actions/ToastActions';
import VisualComposerMessageActions from 'chat/actions/VisualComposerMessageActions';
import VisualComposerPrimaryButton from 'chat/components/FullPageComposer/VisualComposerPrimaryButton';

import { Target } from 'entities/Message';
import { ContentSourcesContent_me_composer_contentSourcesContent_items_downloadPreview as DownloadPreview } from 'user/queries/__generated__/ContentSourcesContent';

import 'chat/components/styles/VisualComposer.scss';
import {
  ComposerContext,
  UIEventContext,
} from 'chat/components/SplitComposer/components/ComposerContext';
// @ts-ignore
import ComposerIntegrations from './ComposerIntegrations';
// @ts-ignore
import TranslateButton from './TranslateButton';
import VisualComposerScheduler from './VisualComposerScheduler';
import VisualComposerFooter from './VisualComposerFooter';
import { getLanguageSets, TranslatedMessagePayload } from 'translations/actions';
import {
  PutMessageInput,
  LanguageSetFiltersInput,
  LanguageSetInput,
  LanguageSetInputTargetType,
  LanguageSetRecipientsRole,
} from 'globalTypes';
import { MessageFilterType } from 'types/typescript/messagingTypes';
import PAID_ORG_STATUS from 'user/queries/paidOrgStatusQuery.gql';

import { GetLanguageSet_translations_getLanguageSet_data_languageCounts as LanguageCounts } from 'translations/queries/__generated__/GetLanguageSet';
import { SuccessResponse } from 'translations/types';
import SettingsStore from 'stores/SettingsStore';
import Text from 'components/Text';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Row from 'components/Row';
import MediaObject from 'components/MediaObject';
import Button from 'components/ButtonDeprecated';
import MetricTracking, { MetricName, MetricTagValue, boolTagValue } from 'lib/MetricTracking';
import { UploadRequestHandler, ThumbnailHandler } from 'lib/fileUploadHelper';

type ComposerLinkPreviewType = {
  title: string;
  description: string;
  url: string;
  image: string;
  image_width: string;
  image_height: string;
  rich_content_type: string;
  rich_content_url: string;
  downloadPreview?: DownloadPreview;
};

type Props = {
  autoFocus?: boolean;
  className?: string | null;
  charLimit: number;
  charLimitWithFiles: number;
  constrainMaxHeight?: boolean;
  districtIsSelected?: boolean;
  draftAnnouncement?: Message;
  onMessageSent: (targets: Target[]) => void;
  targets: Target[];
  onBack: () => void;
  orgUuid?: string;
  orgName?: string;
  userHasPaidOrg: boolean;
  onRequestHide?: () => void;
  onTextChange: (text: string) => void;
  onScheduledTimeChange: (date?: Date | null) => void;
  text: string;
  scheduledTime?: Date;
  uuidForNewMessage?: string;
  linkPreviews?: OrderedMap<string, ComposerLinkPreviewType> | null;
  onRemoveLinkPreview?: (preview: ComposerLinkPreviewType) => void;
  isUrgentMessage?: boolean;
  addLinkedContent: (content: any) => void;
  uploadRequest: UploadRequestHandler;
  loadThumbnail: ThumbnailHandler;
  filePreview?: React.ReactNode;
  fileUuid?: string;
} & ReduxStateProps;

type ReduxStateProps = {
  totalRecipientCount: number;
};

type State = {
  latestTranslation?: string;
  languageCounts: LanguageCounts[];
  shouldTranslate: boolean;
  translationsPreviewActive: boolean;
  translatedMessageResponse?: TranslatedMessagePayload;
};

export class VisualComposer extends React.Component<Props, State> {
  state: State = {
    shouldTranslate: this.props.userHasPaidOrg,
    translationsPreviewActive: false,
    languageCounts: [],
  };

  async componentDidMount() {
    const { targets } = this.props;

    if (!isEmpty(targets)) {
      this.updateLanguageSet(targets);
    }
  }

  async componentDidUpdate(prevProps: Props) {
    const { scheduledTime, onScheduledTimeChange, targets } = this.props;
    if (scheduledTime && this.isSchedulingDisabled()) {
      onScheduledTimeChange(null);
    }

    if (!isEqual(targets.sort(), prevProps.targets.sort())) {
      this.updateLanguageSet(targets);
    }
  }

  toggleTranslate = (shouldTranslate: boolean) => {
    this.setState({ shouldTranslate });
  };

  updateLanguageSet = async (targets: Target[]) => {
    if (this.hasNewTranslationsEnabled()) {
      const result = await getLanguageSets(this.languageSetInputs(targets));
      if (result.success) {
        this.setState({
          languageCounts: result.items,
        });
      }
    }
  };

  recipientRoleToEnum = (role: MessageFilterType): LanguageSetRecipientsRole | null => {
    switch (role.value.id) {
      case 'admin':
        return LanguageSetRecipientsRole.admin;
      case 'parent':
        return LanguageSetRecipientsRole.parent;
      case 'student':
        return LanguageSetRecipientsRole.student;
      case 'teacher':
        return LanguageSetRecipientsRole.teacher;
      default:
        return null;
    }
  };

  targetFilters = (
    targetSelectedFilters: MessageFilterType[] | null
  ): LanguageSetFiltersInput | null => {
    if (targetSelectedFilters === null) {
      return null;
    }
    return {
      roles: compact(targetSelectedFilters.map(this.recipientRoleToEnum)),
    };
  };

  languageSetInputs = (targets: Target[]): LanguageSetInput[] =>
    compact(
      targets.map(target => {
        switch (target.type) {
          case 'district_target':
            return {
              targetType: LanguageSetInputTargetType.organization,
              targetUuid: target.uuid,
              filters: this.targetFilters(target.selectedFilters),
            };
          case 'group_target':
            return {
              targetType: LanguageSetInputTargetType.group,
              targetUuid: target.uuid,
              filters: this.targetFilters(target.selectedFilters),
            };
          case 'group_chat_target':
            return {
              targetType: LanguageSetInputTargetType.groupChat,
              targetUuid: target.uuid,
              filters: null,
            };
          case 'individual_target':
            return {
              targetType: LanguageSetInputTargetType.individual,
              targetUuid: target.uuid,
              filters: null,
            };
          default:
            // We only support the first two types for right now
            return null;
        }
      })
    );

  handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.currentTarget.value;
    const { onTextChange } = this.props;
    onTextChange(text);
  };

  hasNewTranslationsEnabled = () =>
    CurrentUser.isSignedIn() &&
    CurrentUser.getUser() &&
    CurrentUser.getUser().hasFeature('2020.dec.preferred_language_translation_v0');

  handleMessageSent = () => {
    const { onTextChange, onMessageSent, targets } = this.props;
    this.incrementMetricCount('composer.composition.view.sent.success');
    onTextChange('');
    onMessageSent(targets);
  };

  handleMessageSentError = (error: Error) => {
    this.incrementMetricCount('composer.composition.view.sent.failed');
    ToastActions.showAPIError(error);
    const dwmPrefix = this.props.districtIsSelected ? 'DWM - ' : '';
    console.warn(error);
    console.error(`${dwmPrefix}VisualComposer sendMessage failed`);
  };

  incrementMetricCount = (name: MetricName) => {
    const submissionMechanism: MetricTagValue = (() => {
      if (this.props.scheduledTime) {
        if (this.props.draftAnnouncement) {
          return 'update_scheduled_message';
        } else {
          return 'new_scheduled_message';
        }
      } else if (
        this.props.targets.filter(target => target.type === 'group_chat_target').length > 0
      ) {
        return 'group_chat';
      }

      return 'new_message';
    })();

    MetricTracking.increment(name, {
      tags: {
        is_urgent: boolTagValue(!!this.props.isUrgentMessage),
        has_attachment: boolTagValue(!!this.props.fileUuid),
        has_plt_translations: boolTagValue(!!this.state.latestTranslation),
        is_scheduled: boolTagValue(!!this.props.scheduledTime),
        message_submission_type: submissionMechanism,
      },
    });
  };

  targetLanguageCodes = () =>
    this.state.languageCounts
      .map(languageCount => languageCount.languageCode)
      .filter(code => code !== 'none');

  putMessageInputFromState(senderType: string, senderUuid: string): PutMessageInput {
    const {
      targets,
      text,
      fileUuid,
      uuidForNewMessage,
      linkPreviews,
      isUrgentMessage,
    } = this.props;
    const recipients = putMessageRecipientsForTargets(targets);
    const previews = linkPreviews ? linkPreviews.toList().toArray() : [];
    const fileUuids = [];
    if (fileUuid) {
      fileUuids.push(fileUuid);
    }

    return {
      recipients,
      message: {
        body: text,
        fileUuids,
        linkPreviews: previews.map(preview => {
          return {
            url: preview.url,
            title: preview.title,
            description: preview.description,
            image: preview.image,
            image_width: preview.image_width ? String(preview.image_width) : null,
            image_height: preview.image_height ? String(preview.image_height) : null,
            rich_content_type: preview.rich_content_type,
            rich_content_url: preview.rich_content_url,
          };
        }),
        urgent: isUrgentMessage || false,
        translations:
          this.hasNewTranslationsEnabled() && this.state.shouldTranslate
            ? {
                targetLanguageCodes: this.targetLanguageCodes(),
              }
            : null,
      },
      uuid: uuidForNewMessage,
      sender: {
        type: senderType,
        uuid: senderUuid,
      },
    };
  }

  sendMessage = (propagateThrow: boolean = false): Promise<void> => {
    const {
      scheduledTime,
      uuidForNewMessage,
      draftAnnouncement,
      districtIsSelected,
      orgUuid,
    } = this.props;

    const promiseHandler = (
      promise: Promise<void>,
      errMsg = 'VisualComposer sendMessage failed'
    ) => {
      return promise.then(this.handleMessageSent, this.handleMessageSentError).catch(err => {
        errorLogger(errMsg)(err);
        return propagateThrow ? Promise.reject(err) : undefined;
      });
    };

    const putMessageInput = this.putMessageInputFromState('user', CurrentUser.getUserUuid());
    if (scheduledTime) {
      if (draftAnnouncement) {
        return VisualComposerMessageActions.updateScheduledMessage({
          uuid: uuidForNewMessage ?? '',
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: (scheduledTime as unknown) as string,
          },
        })
          .then(() => {
            // Remove the original message in our store because send-to-few creates a new message
            dispatcher.dispatch({
              type: ActionTypes.DESTROY_GROUP_SCHEDULED_MESSAGE,
              message: draftAnnouncement,
            });
            this.handleMessageSent();
          }, this.handleMessageSentError)
          .catch(err => {
            errorLogger('VisualComposer sendMessage failed')(err);
            return propagateThrow ? Promise.reject(err) : undefined;
          });
      } else {
        const schedMessagePromise = VisualComposerMessageActions.scheduleMessage({
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: (scheduledTime as unknown) as string,
          },
        });
        return promiseHandler(schedMessagePromise);
      }
    } else if (districtIsSelected) {
      if (!orgUuid) {
        // just making sure this never causes unhandled promises.
        return propagateThrow ? Promise.reject() : Promise.resolve();
      }
      const districtPutMessageInput = this.putMessageInputFromState('district', orgUuid);
      const putDWMMessagePromise = VisualComposerMessageActions.putMessage({
        input: districtPutMessageInput,
      });
      return promiseHandler(
        putDWMMessagePromise,
        'DWM - VisualComposer sendMessage promise failed'
      );
    } else {
      const putMessagePromise = VisualComposerMessageActions.putMessage({ input: putMessageInput });
      return promiseHandler(putMessagePromise);
    }
  };

  isValidMessage = () => {
    const { filePreview, fileUuid, targets, text } = this.props;

    const isAddressed = targets && targets.length > 0;

    const uploadComplete = filePreview ? !!fileUuid : true;

    const hasText = text.trim().length > 0;

    return isAddressed && uploadComplete && (hasText || !!fileUuid);
  };

  isSchedulingDisabled = () => {
    const { districtIsSelected, targets } = this.props;
    return districtIsSelected || !allTargetsAreGroups(targets);
  };

  setTranslationPreview = (translateMessageResponse: SuccessResponse<TranslatedMessagePayload>) => {
    this.setState({
      translationsPreviewActive: true,
      translatedMessageResponse: translateMessageResponse,
    });
  };

  backToOriginal = () => {
    this.setState({
      translationsPreviewActive: false,
      translatedMessageResponse: undefined,
    });
  };

  getLanguagePreviewTitle = () => {
    const languages = SettingsStore.getLanguages();
    const secondaryLanguage = languages?.secondary;
    const { translationsPreviewActive, translatedMessageResponse } = this.state;

    if (secondaryLanguage && translationsPreviewActive && translatedMessageResponse) {
      if (translatedMessageResponse.languageCode === 'none') return 'No Preference';
      return secondaryLanguage[translatedMessageResponse?.languageCode || 'en'];
    }
    return 'en';
  };

  renderTranslateButton(uiEventContext: UIEventContext) {
    const { text, onTextChange, targets } = this.props;
    const { shouldTranslate, languageCounts, translationsPreviewActive } = this.state;

    return (
      <TranslateButton
        shouldTranslate={shouldTranslate}
        languageCounts={languageCounts}
        toggleTranslate={this.toggleTranslate}
        onTextChange={onTextChange}
        onLangChange={(lang: string) => this.setState({ latestTranslation: lang })}
        eventKey="announcements.announcement_modal.universal_composer.show_translation_box.click"
        eventProperties={uiEventContext}
        dataTestSelector="visual_composer_translation_button"
        textToTranslate={text}
        translationsPreviewActive={translationsPreviewActive}
        setTranslationPreview={this.setTranslationPreview}
        backToOriginalCallback={this.backToOriginal}
        hasTargets={!isEmpty(targets)}
      />
    );
  }

  renderLinkPreviews(linkPreviews: OrderedMap<string, ComposerLinkPreviewType>) {
    const { onRemoveLinkPreview } = this.props;
    const items = linkPreviews.valueSeq().map(preview => {
      if (!preview) {
        return;
      }

      return (
        <ComposerLinkPreview
          key={preview.url}
          linkPreview={preview}
          onRemove={() => {
            if (onRemoveLinkPreview) {
              onRemoveLinkPreview(preview);
            }
          }}
        />
      );
    });
    return <div className="VisualComposer-contentPreviewItems">{items}</div>;
  }

  render() {
    const {
      charLimit,
      charLimitWithFiles,
      constrainMaxHeight,
      autoFocus,
      text,
      linkPreviews,
      districtIsSelected,
      isUrgentMessage,
      orgUuid,
      onRequestHide,
      totalRecipientCount,
      targets,
      orgName,
      addLinkedContent,
      scheduledTime,
      onScheduledTimeChange,
      uploadRequest,
      loadThumbnail,
      filePreview,
      fileUuid,
    } = this.props;
    const { latestTranslation, translationsPreviewActive, translatedMessageResponse } = this.state;

    const charactersRemaining = CharacterCount.getRemainingCharactersForText(
      charLimit,
      charLimitWithFiles,
      text.trim(),
      !!fileUuid
    );

    const linkedContentSection =
      linkPreviews && linkPreviews.size > 0 ? this.renderLinkPreviews(linkPreviews) : null;

    const languagePreviewTitle = this.getLanguagePreviewTitle();
    const composerValue =
      translationsPreviewActive && translatedMessageResponse
        ? translatedMessageResponse.translatedText
        : text;

    return (
      <ComposerContext.Consumer>
        {composerContext => (
          <div
            className={cx('VisualComposer', this.props.className)}
            data-test-selector="visual_composer_textarea">
            <div className="VisualComposer-composerWrapper">
              <div className="VisualComposer-wrap">
                <div className="VisualComposer-composerContainer">
                  {translatedMessageResponse?.languageCode === 'none' ? (
                    <Box size="medium">
                      <BoxBody size="xsmall">
                        <Row size="small">
                          <MediaObject figure="right">
                            <Text align="left" weight="semibold" size="medium">
                              {languagePreviewTitle}
                            </Text>
                            <Button size="xxxsmall" onClick={this.backToOriginal}>
                              Close
                            </Button>
                          </MediaObject>
                        </Row>
                        <Text align="left">
                          {translatedMessageResponse.translatedText}{' '}
                          <Link to={LinkConstants.TRANSLATIONS_TOOLTIP_HELP}>Learn More.</Link>
                        </Text>
                      </BoxBody>
                    </Box>
                  ) : (
                    <Composer
                      className="VisualComposer"
                      placeholder="Type your message"
                      autogrow
                      constrainMaxHeight={constrainMaxHeight}
                      value={composerValue}
                      disabled={translationsPreviewActive}
                      autoFocus={autoFocus}
                      error={charactersRemaining < 0}
                      showTitle={translationsPreviewActive}
                      title={languagePreviewTitle}
                      hideTitleAction={this.backToOriginal}
                      onChange={this.handleTextChange}>
                      <div className="VisualComposer-filePreview">{filePreview}</div>
                      {linkedContentSection}
                    </Composer>
                  )}
                </div>
                <VisualComposerFooter
                  className="VisualComposer-buttonContainer"
                  scheduleButton={
                    !translationsPreviewActive ? (
                      <VisualComposerScheduler
                        scheduledTime={scheduledTime}
                        disabled={this.isSchedulingDisabled()}
                        onSetScheduledTime={onScheduledTimeChange}
                      />
                    ) : (
                      undefined
                    )
                  }
                  attachButton={
                    !translationsPreviewActive ? (
                      <ComposerIntegrations
                        addLinkedContent={addLinkedContent}
                        hasFile={!!filePreview}
                        hasLinks={linkPreviews?.size}
                        uploadRequest={uploadRequest}
                        loadThumbnail={loadThumbnail}
                      />
                    ) : (
                      undefined
                    )
                  }
                  sendButton={
                    !translationsPreviewActive ? (
                      <VisualComposerPrimaryButton
                        charactersRemaining={charactersRemaining}
                        districtIsSelected={districtIsSelected}
                        isScheduling={!!scheduledTime}
                        isUrgentMessage={isUrgentMessage}
                        latestTranslation={latestTranslation}
                        messageIsValid={this.isValidMessage()}
                        uiEventContext={composerContext.getUIEventContext()}
                        onContinueConfirm={() => {
                          this.sendMessage(true)
                            .then(() => {
                              EventTracking.trackKey(
                                'announcements.composer.confirm_send.send.click',
                                {
                                  ...composerContext.getUIEventContext(),
                                  organization_uuid: orgUuid,
                                }
                              );
                            })
                            .catch(() => {
                              console.error(
                                `${
                                  isUrgentMessage ? 'URGENT' : 'DWM'
                                } - missing district uuid - could not write district message`
                              );
                            });
                          if (onRequestHide) {
                            onRequestHide();
                          }
                        }}
                        onSchedule={() => this.sendMessage()}
                        onSend={() => this.sendMessage()}
                        orgName={orgName}
                        orgUuid={orgUuid}
                        targets={targets}
                        totalRecipientCount={totalRecipientCount}
                      />
                    ) : (
                      undefined
                    )
                  }
                  translateButton={this.renderTranslateButton(composerContext.getUIEventContext())}
                  charactersRemaining={charactersRemaining}
                />
              </div>
            </div>
          </div>
        )}
      </ComposerContext.Consumer>
    );
  }
}

const mapStateToProps = (state: any): ReduxStateProps => {
  return {
    totalRecipientCount: state.roleBasedMessagingSearch.totalRecipientCount,
  };
};

const withPaidStatus = graphql(PAID_ORG_STATUS, {
  props: ({ data }) => {
    const foundPaidOrg = (userData: any) => {
      return (
        !userData?.me?.organizations?.every((o: any) => !o.paid) ||
        !userData?.me?.classes?.every((c: any) => !c.organization || !c.organization.paid)
      );
    };
    const hasPaidOrg = foundPaidOrg(data);

    return { userHasPaidOrg: hasPaidOrg };
  },
});

export default compose(connect(mapStateToProps), withPaidStatus)(VisualComposer);
